import type { Graphics } from 'pixi.js';
import { PAPER, lerpColor } from '../../color';
import { sag } from '../../ink';
import type { Body, WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const FRESNEL = [0.3, 0.52, 0.72, 0.9];
const LEAVES = 6;
const TARGET_BULBS = 16;
const RUNES = 4;
const CABLE_SAG = 0.05;
const CHASE = 16;
const MEDALS = 8;

function leaf(g: Graphics, x: number, y: number, dx: number, dy: number, len: number, wid: number): void {
  const nx = -dy;
  const ny = dx;
  g.poly([x + dx * len, y + dy * len, x + nx * wid, y + ny * wid, x - dx * len * 0.6, y - dy * len * 0.6, x - nx * wid, y - ny * wid], true);
}

function laurel(g: Graphics, body: Body, ring: number, size: number): void {
  for (const side of [-1, 1]) {
    for (let i = 0; i < LEAVES; i++) {
      const a = Math.PI / 2 + side * (0.4 + (i / LEAVES) * 2.2);
      const x = body.x + Math.cos(a) * ring * body.scaleX;
      const y = body.y + Math.sin(a) * ring * body.scaleY;
      const tx = -Math.sin(a) * side + Math.cos(a) * 0.35;
      const ty = Math.cos(a) * side + Math.sin(a) * 0.35;
      const n = Math.hypot(tx, ty) || 1;
      leaf(g, x, y, tx / n, ty / n, size, size * 0.38);
    }
  }
}

function confetti(g: Graphics, x: number, y: number, w: number, h: number, rot: number): void {
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  const pts: number[] = [];
  for (const [u, v] of [
    [-w, -h],
    [w, -h],
    [w, h],
    [-w, h],
  ] as const) {
    pts.push(x + u * c - v * s, y + u * s + v * c);
  }
  g.poly(pts, true);
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    for (const s of FRESNEL) g.ellipse(body.x, body.y, r * s * body.scaleX, r * s * body.scaleY);
    g.stroke({ width: Math.max(1, r * 0.04), color: lerpColor(color, pal.ink, 0.45), alpha: 0.35 * alpha });
    g.ellipse(body.x, body.y, r * 0.17 * body.scaleX, r * 0.17 * body.scaleY).fill({ color: lerpColor(color, PAPER, 0.6), alpha: 0.5 * alpha });
    k.highlight(body, 0.55 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    k.trace(body);
    g.fill({ color: fill, alpha });
    g.ellipse(body.x, body.y, r * 0.84 * body.scaleX, r * 0.84 * body.scaleY);
    g.stroke({ width: Math.max(1, r * 0.05), color: pal.heldShade, alpha: 0.9 * alpha });
    laurel(g, body, r * 0.64, r * 0.13);
    g.fill({ color: pal.heldShade, alpha: 0.85 * alpha });
    g.star(body.x, body.y, 5, r * 0.22, r * 0.1, 0).fill({ color: pal.heldLight, alpha: 0.9 * alpha });
    g.stroke({ width: Math.max(1, r * 0.025), color: pal.heldShade, alpha: 0.8 * alpha });
    k.highlight(body, 0.5 * alpha);
    k.trace(body);
    g.stroke({ width, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    k.trace(body);
    g.fill({ color: pal.ink, alpha: 0.32 * alpha });
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.08 * alpha });
    const bulb = lerpColor(pal.held, pal.rim, 0.35);
    k.ringDots(body, reach, TARGET_BULBS, spin, (x, y) => {
      g.circle(x, y, Math.max(3, k.unit * 0.02)).fill({ color: bulb, alpha: 0.13 * alpha });
      g.circle(x, y, Math.max(1.4, k.unit * 0.009)).fill({ color: bulb, alpha: 0.6 * alpha });
    });
    k.ringDots(body, reach + k.unit * 0.045, RUNES, Math.PI / 4 - spin * 0.5, (x, y, _i, a) => {
      g.star(x, y, 4, Math.max(3, k.unit * 0.016), Math.max(1.2, k.unit * 0.006), a).fill({ color: pal.spot, alpha: 0.5 * alpha });
    });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.8 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    const tone = last ? pal.spot : pal.held;
    if (on) g.circle(x, y, radius * 1.9).fill({ color: tone, alpha: 0.18 * alpha });
    g.circle(x, y, radius).fill({ color: on ? lerpColor(tone, PAPER, 0.25) : lerpColor(pal.bg, pal.rim, 0.16), alpha: (on ? 0.9 : 1) * alpha });
    g.stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.45) * alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    const cable = (): void => {
      g.moveTo(points[0]!, points[1]!);
      for (let i = 2; i < count; i += 2) {
        const x0 = points[i - 2]!;
        const y0 = points[i - 1]!;
        const x1 = points[i]!;
        const y1 = points[i + 1]!;
        const mid = sag(x0, y0, x1, y1, CABLE_SAG);
        g.quadraticCurveTo(2 * mid.x - (x0 + x1) / 2, 2 * mid.y - (y0 + y1) / 2, x1, y1);
      }
    };
    cable();
    g.stroke({ width: Math.max(2.4, k.unit * 0.01), color: pal.ink, alpha: alpha * 3, cap: 'round', join: 'round' });
    cable();
    g.stroke({ width: 1.2, color: pal.path, alpha: alpha * 2.2, cap: 'round', join: 'round' });
    const tape = lerpColor(pal.path, pal.held, 0.4);
    const half = Math.max(2.5, k.unit * 0.012);
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const x1 = points[i]!;
      const y1 = points[i + 1]!;
      const mid = sag(x0, y0, x1, y1, CABLE_SAG);
      confetti(g, mid.x, mid.y, half * 0.45, half, Math.atan2(y1 - y0, x1 - x0));
    }
    g.fill({ color: tape, alpha: alpha * 2.6 });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    if (i % 3 === 0) g.star(x, y, 4, size * 1.3, size * 0.32, rot);
    else confetti(g, x, y, size * 0.75, size * 0.36, rot);
    g.fill({ color, alpha });
  },

  milestone(g, m, pal) {
    const bulb = lerpColor(pal.held, pal.rim, 0.35);
    const head = m.grow * CHASE * 0.6;
    const ring = m.r * 1.08;
    for (let i = 0; i < CHASE; i++) {
      const a = (i / CHASE) * TAU - Math.PI / 2;
      const x = m.x + Math.cos(a) * ring;
      const y = m.y + Math.sin(a) * ring;
      const d = Math.min(Math.abs(i - head), CHASE - Math.abs(i - head));
      const lit = 0.35 + 0.4 * Math.exp(-(d * d) / 18);
      g.circle(x, y, Math.max(2.4, m.unit * 0.012)).fill({ color: bulb, alpha: m.fade * lit * 0.8 });
    }
    const show = m.fade * Math.min(1, Math.max(0, (m.grow - 0.3) / 0.4));
    if (show <= 0) return;
    const orbit = m.r * 1.42;
    const size = Math.max(4, m.unit * 0.026);
    for (let i = 0; i < MEDALS; i++) {
      const a = (i / MEDALS) * TAU + 0.2;
      const x = m.x + Math.cos(a) * orbit;
      const y = m.y + Math.sin(a) * orbit;
      const tone = pal.sparks[i % pal.sparks.length] ?? pal.held;
      g.circle(x, y, size).fill({ color: pal.ink, alpha: 0.55 * show });
      g.stroke({ width: Math.max(1, m.width * 0.6), color: tone, alpha: 0.6 * show });
      if (i % 2 === 0) g.star(x, y, 5, size * 0.6, size * 0.26, 0);
      else g.circle(x, y, size * 0.36);
      g.fill({ color: tone, alpha: 0.55 * show });
    }
  },

  trail(g, x, y, size, angle) {
    g.star(x, y, 4, size * 1.2, size * 0.3, angle);
  },
};
